import React, { useState, useEffect, useCallback } from 'react';
import Card from './Card';
import Button from './Button';
import { Calendar, DollarSign, TrendingUp, AlertCircle, RefreshCw } from 'lucide-react';
import { apiUrl } from '../config';

interface CostData {
  date: string;
  cost: number;
  service: string;
  project?: string;
}

interface CostBreakdown {
  service: string;
  cost: number;
  percentage: number;
}

interface CostMonitoringProps {
  className?: string;
  showHeader?: boolean;
  compact?: boolean;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export default function CostMonitoring({
  className = '',
  showHeader = true,
  compact = false,
}: CostMonitoringProps) {
  const [costData, setCostData] = useState<CostData[]>([]);
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdown[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('daily');
  const [totalCost, setTotalCost] = useState(0);

  const fetchCostData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Map weekly to daily granularity since AWS Cost Explorer doesn't support weekly
      const apiPeriod = period === 'weekly' ? 'daily' : period;
      const response = await fetch(apiUrl(`cost-breakdown?period=${apiPeriod}`));

      if (!response.ok) {
        throw new Error(`Failed to fetch cost data: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setCostData(data.data || []);
        setCostBreakdown(data.breakdown || []);
        setTotalCost(data.totalCost || 0);
      } else {
        throw new Error(data.message || 'Failed to fetch cost data');
      }
    } catch (err) {
      console.error('Error fetching cost data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch cost data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchCostData();
  }, [fetchCostData]);

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (compact) {
    return (
      <Card className={className}>
        <div className='p-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <DollarSign className='h-4 w-4 text-green-600' />
              <span className='text-sm font-medium'>Total Cost</span>
            </div>
            <div className='text-right'>
              <div className='text-lg font-bold'>{formatCurrency(totalCost)}</div>
              <div className='text-xs text-muted-foreground'>Last {period}</div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className={className}>
      {showHeader && (
        <div className='flex items-center justify-between mb-6'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>Cost Monitoring</h2>
            <p className='text-muted-foreground'>Track and analyze your AWS infrastructure costs</p>
          </div>
          <div className='flex items-center space-x-2'>
            <select
              value={period}
              onChange={e => handlePeriodChange(e.target.value)}
              className='px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900'
            >
              <option value='daily'>Daily</option>
              <option value='weekly'>Weekly</option>
              <option value='monthly'>Monthly</option>
            </select>
            <Button variant='outline' onClick={fetchCostData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      )}

      {error && (
        <Card className='mb-6'>
          <div className='p-4'>
            <div className='flex items-center space-x-2 text-red-600'>
              <AlertCircle className='h-4 w-4' />
              <span className='text-sm'>{error}</span>
            </div>
          </div>
        </Card>
      )}

      <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
        {/* Total Cost Card */}
        <Card>
          <div className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-600'>Total Cost</p>
                <p className='text-2xl font-bold'>{formatCurrency(totalCost)}</p>
                <p className='text-xs text-gray-500'>Last {period} period</p>
              </div>
              <DollarSign className='h-8 w-8 text-gray-400' />
            </div>
          </div>
        </Card>

        {/* Cost Trend Card */}
        <Card>
          <div className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-600'>Cost Trend</p>
                <p className='text-2xl font-bold'>
                  {costData.length > 1 ? (
                    costData[costData.length - 1].cost > costData[costData.length - 2].cost ? (
                      <span className='text-red-600'>↗</span>
                    ) : (
                      <span className='text-green-600'>↘</span>
                    )
                  ) : (
                    <span className='text-gray-600'>-</span>
                  )}
                </p>
                <p className='text-xs text-gray-500'>Compared to previous period</p>
              </div>
              <TrendingUp className='h-8 w-8 text-gray-400' />
            </div>
          </div>
        </Card>

        {/* Services Count Card */}
        <Card>
          <div className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-gray-600'>Services</p>
                <p className='text-2xl font-bold'>{costBreakdown.length}</p>
                <p className='text-xs text-gray-500'>Active services</p>
              </div>
              <Calendar className='h-8 w-8 text-gray-400' />
            </div>
          </div>
        </Card>
      </div>

      {/* Cost Data Table */}
      {costBreakdown.length > 0 && (
        <Card className='mt-6'>
          <div className='p-6'>
            <h3 className='text-lg font-semibold mb-4'>Service Breakdown</h3>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b'>
                    <th className='text-left py-2'>Service</th>
                    <th className='text-right py-2'>Cost</th>
                    <th className='text-right py-2'>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {costBreakdown.map((item, index) => (
                    <tr key={item.service} className='border-b'>
                      <td className='py-2'>
                        <div className='flex items-center space-x-2'>
                          <div className={`w-3 h-3 rounded-full chart-color-${index % 10}`} />
                          <span className='font-medium'>{item.service}</span>
                        </div>
                      </td>
                      <td className='text-right py-2 font-medium'>{formatCurrency(item.cost)}</td>
                      <td className='text-right py-2 text-gray-500'>
                        {item.percentage.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {loading && (
        <Card className='mt-6'>
          <div className='p-6 text-center'>
            <RefreshCw className='h-6 w-6 animate-spin mx-auto mb-2' />
            <p className='text-gray-600'>Loading cost data...</p>
          </div>
        </Card>
      )}

      {!loading && costBreakdown.length === 0 && !error && (
        <Card className='mt-6'>
          <div className='p-6 text-center'>
            <p className='text-gray-600'>No cost data available</p>
            <p className='text-sm text-gray-500 mt-1'>
              Cost data may take up to 24 hours to appear
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
